import { QuestionCircleOutlined } from '@ant-design/icons'
import { Col, Form, Input, InputNumber, Modal, Row, Switch, Tag, Tooltip } from 'antd'
import { IBaseFormProps } from 'Modules'
import { useMemo } from 'react'
import { isAddress } from 'viem'

import CopyIcon from 'component/button/copy-icon'
import DatePicker from 'component/date-picker/date-picker'
import MarkdownEditor from 'component/markdown-editor/markdown-editor'
import SelectCountry from 'component/select/select-country'
import {
  PTO_V3_CONTRACT_NAME_MAX_LENGTH,
  PTO_V3_DISPLAY_TOOLTIP,
  PTO_V3_SELLING_STATUS_CONFIG,
  PtoV3SellingStatusEnum,
} from 'constants/pto-v3'
import { PTO_V3_SYMBOL_MAX_LENGTH } from 'constants/pto-v3'
import { convertNumberToDateTime } from 'utils/datetime'

interface IBaseInformationV3Props extends IBaseFormProps {
  id?: string | null | undefined
  switchChecked?: boolean
  setSwitchChecked?: (e: boolean) => void
  sellingStatus?: PtoV3SellingStatusEnum
}

/**
 * PTO v3 (ERC-3643) basic information - same as PTO v2 without Royalty and with a
 * Token Symbol next to Name (passed to PTOOrchestrator.createPTO by the BE calldata).
 */
const BaseInformation = ({
  mode = 'edit',
  edit = true,
  form,
  switchChecked = false,
  setSwitchChecked,
  sellingStatus,
}: IBaseInformationV3Props) => {
  const isModeCreate = useMemo(() => {
    return mode === 'create'
  }, [mode])

  // copy buttons on the address inputs (edit mode only - nothing to copy while creating)
  const ownerAddressValue = Form.useWatch('ownerAddress', form)
  const contractAddressValue = Form.useWatch('contractAddress', form)
  const onchainIdValue = Form.useWatch('onchainId', form)
  const copyAddon = (value?: string) =>
    !isModeCreate && value ? <CopyIcon text={value} /> : undefined

  const sellingStatusConfig =
    PTO_V3_SELLING_STATUS_CONFIG[sellingStatus ?? PtoV3SellingStatusEnum.OFF_SALE] ||
    PTO_V3_SELLING_STATUS_CONFIG[PtoV3SellingStatusEnum.OFF_SALE]

  const handleSwitchClick = () => {
    Modal.confirm({
      title: 'Are you sure you want to change the display?',
      content: PTO_V3_DISPLAY_TOOLTIP,
      onOk: () => {
        setSwitchChecked?.(!switchChecked)
      },
      okText: 'Yes',
      cancelText: 'No',
      centered: true,
    })
  }

  return (
    <fieldset>
      <legend>Basic Information</legend>
      <Row gutter={24}>
        {/* Name */}
        <Col span={12}>
          <Form.Item
            label={
              <>
                Name
                <Tooltip
                  title={
                    <div className="tooltip-box">
                      <p className="tooltip-text">
                        This is your PTO name, please enter your chosen name as text
                      </p>
                    </div>
                  }
                  placement="right"
                >
                  <QuestionCircleOutlined className="tooltip-icon" />
                </Tooltip>
              </>
            }
            name="name"
            rules={[
              { required: true, message: 'Please input the name!' },
              {
                min: 3,
                max: 50,
                message: 'Name must be between 3 and 50 characters!',
              },
            ]}
          >
            <Input
              style={{ color: 'rgba(0, 0, 0, 0.85)' }}
              disabled={!edit}
              placeholder="Enter the name"
            />
          </Form.Item>
        </Col>

        {/* Token Symbol */}
        <Col span={12}>
          <Form.Item
            label={
              <>
                Token Symbol
                <Tooltip
                  title={
                    <div className="tooltip-box">
                      <p className="tooltip-text">
                        This is the ERC-3643 token symbol of your PTO, it is used when deploying the
                        token contract
                      </p>
                    </div>
                  }
                  placement="right"
                >
                  <QuestionCircleOutlined className="tooltip-icon" />
                </Tooltip>
              </>
            }
            name="symbol"
            rules={[
              { required: true, message: 'Please input the token symbol!' },
              {
                max: PTO_V3_SYMBOL_MAX_LENGTH,
                message: `Token symbol must be at most ${PTO_V3_SYMBOL_MAX_LENGTH} characters!`,
              },
            ]}
          >
            <Input
              style={{ color: 'rgba(0, 0, 0, 0.85)' }}
              disabled={!isModeCreate}
              maxLength={PTO_V3_SYMBOL_MAX_LENGTH}
              placeholder="Enter token symbol"
            />
          </Form.Item>
        </Col>

        {/* Contract Name */}
        <Col span={12}>
          <Form.Item
            label={
              <>
                Contract Name
                <Tooltip
                  title={
                    <div className="tooltip-box">
                      <p className="tooltip-text">
                        This is your PTO collection contract name, it is used when deploying the
                        collection contract
                      </p>
                    </div>
                  }
                  placement="right"
                >
                  <QuestionCircleOutlined className="tooltip-icon" />
                </Tooltip>
              </>
            }
            name="contractName"
            rules={[
              { required: true, message: 'Please input the contract name!' },
              {
                max: PTO_V3_CONTRACT_NAME_MAX_LENGTH,
                message: `Contract name must be at most ${PTO_V3_CONTRACT_NAME_MAX_LENGTH} characters!`,
              },
            ]}
          >
            <Input
              style={{ color: 'rgba(0, 0, 0, 0.85)' }}
              disabled={!isModeCreate}
              maxLength={PTO_V3_CONTRACT_NAME_MAX_LENGTH}
              placeholder="Enter contract name"
            />
          </Form.Item>
        </Col>

        {/* Total Supply */}
        <Col span={12}>
          <Form.Item
            label={
              <>
                Total Supply
                <Tooltip
                  title={
                    <div className="tooltip-box">
                      <p className="tooltip-text">
                        This is your PTO total supply, please enter your chosen total supply as
                        number
                      </p>
                    </div>
                  }
                  placement="right"
                >
                  <QuestionCircleOutlined className="tooltip-icon" />
                </Tooltip>
              </>
            }
            name="totalSupply"
            rules={[
              { required: true, message: 'Please input the total supply!' },
              {
                type: 'number',
                min: 1,
                message: 'Total supply must be greater than or equal to 1!',
              },
            ]}
          >
            <InputNumber
              style={{ color: 'rgba(0, 0, 0, 0.85)', width: '100%' }}
              min={1}
              disabled={!isModeCreate}
              precision={0}
              placeholder="Enter total supply"
            />
          </Form.Item>
        </Col>

        {/* PTO Type - commented out for v3: rental PTO (staking based) is not
            confirmed for ERC-3643 yet; ptoType defaults to PTO in the create hook.

        <Col span={12}>
          <Form.Item
            label={
              <>
                PTO Type
                <Tooltip
                  title={
                    <div className="tooltip-box">
                      <p className="tooltip-text">Select the type of PTO for this property</p>
                    </div>
                  }
                  placement="right"
                >
                  <QuestionCircleOutlined className="tooltip-icon" />
                </Tooltip>
              </>
            }
            name="ptoType"
            initialValue={PTOTypeEnum.PTO}
            rules={[{ required: true, message: 'Please select the PTO type!' }]}
          >
            PTO type is fixed after creation — only selectable on the create form.
            <Select disabled={!isModeCreate} options={PTO_TYPE_OPTIONS} />
          </Form.Item>
        </Col>
        */}

        {/* Country */}
        <Col span={12}>
          <Form.Item
            name="country"
            label={
              <>
                Country
                <Tooltip
                  title={
                    <div className="tooltip-box">
                      <p className="tooltip-text">
                        This is your PTO country, please select your chosen country
                      </p>
                    </div>
                  }
                  placement="right"
                >
                  <QuestionCircleOutlined className="tooltip-icon" />
                </Tooltip>
              </>
            }
            required
          >
            <SelectCountry
              disabled={!edit}
              value={form.getFieldValue('country')}
              onChange={(value) => {
                form.setFieldsValue({ country: value })
              }}
            />
          </Form.Item>
        </Col>

        {/* Contract owner address */}
        <Col style={{ marginBottom: 12 }} span={12}>
          <Form.Item
            label={
              <>
                Contract Owner Address
                <Tooltip
                  title={
                    <div className="tooltip-box">
                      <p className="tooltip-text">
                        This is your PTO contract owner address, please enter the correct contract
                        owner address and check its information
                      </p>
                    </div>
                  }
                  placement="right"
                >
                  <QuestionCircleOutlined className="tooltip-icon" />
                </Tooltip>
              </>
            }
            name="ownerAddress"
            rules={[
              {
                required: true,
                message: 'Please input the contract owner address!',
              },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve()

                  const valid = isAddress(value)

                  return valid ? Promise.resolve() : Promise.reject('Invalid blockchain address!')
                },
              },
            ]}
            style={{ marginBottom: 12 }}
          >
            <Input
              style={{ color: 'rgba(0, 0, 0, 0.85)' }}
              disabled={!isModeCreate}
              placeholder="Enter the contract owner address"
              addonAfter={copyAddon(ownerAddressValue)}
            />
          </Form.Item>
        </Col>

        {/* Description */}
        <Col span={12}>
          <Row>
            {/* Contract address */}
            <Col span={24}>
              <Form.Item
                label={
                  <>
                    Contract Address
                    <Tooltip
                      title={
                        <div className="tooltip-box">
                          <p className="tooltip-text">
                            This is your PTO collection contract address, it is set after the
                            collection is deployed
                          </p>
                        </div>
                      }
                      placement="right"
                    >
                      <QuestionCircleOutlined className="tooltip-icon" />
                    </Tooltip>
                  </>
                }
                name="contractAddress"
                hidden={isModeCreate}
              >
                <Input
                  style={{ color: 'rgba(0, 0, 0, 0.85)' }}
                  disabled={!isModeCreate}
                  placeholder="Enter the contract address"
                  addonAfter={copyAddon(contractAddressValue)}
                />
              </Form.Item>
            </Col>
            {/* PTO ONCHAINID - set after createPTO */}
            <Col span={24}>
              <Form.Item
                label={
                  <>
                    OnchainID
                    <Tooltip
                      title={
                        <div className="tooltip-box">
                          <p className="tooltip-text">
                            ONCHAINID of the ERC-3643 PTO token, it is set after the token is
                            deployed through the PTO Orchestrator
                          </p>
                        </div>
                      }
                      placement="right"
                    >
                      <QuestionCircleOutlined className="tooltip-icon" />
                    </Tooltip>
                  </>
                }
                name="onchainId"
                hidden={isModeCreate}
              >
                <Input
                  style={{ color: 'rgba(0, 0, 0, 0.85)' }}
                  disabled
                  addonAfter={copyAddon(onchainIdValue)}
                />
              </Form.Item>
            </Col>
            {/* Mô tả */}
            <Col span={24}>
              <Form.Item
                label={
                  <>
                    Description
                    <Tooltip
                      title={
                        <div className="tooltip-box">
                          <p className="tooltip-text">
                            This is your PTO description, please enter your chosen description as
                            text
                          </p>
                        </div>
                      }
                      placement="right"
                    >
                      <QuestionCircleOutlined className="tooltip-icon" />
                    </Tooltip>
                  </>
                }
                name="description"
                rules={[
                  {
                    required: true,
                    message: 'Please input the description!',
                  },
                  {
                    min: 3,
                    max: 500,
                    message: 'Description must be between 3 and 500 characters!',
                  },
                ]}
              >
                <MarkdownEditor disabled={!edit} placeholder="Enter the description" />
              </Form.Item>
            </Col>
            {/* Ngày bán thứ cấp */}
            <Col span={24}>
              <Form.Item
                label={
                  <>
                    Secondary Sale Date
                    <Tooltip
                      title={
                        <div className="tooltip-box">
                          <p className="tooltip-text">
                            This is the date when the PTO can be traded on the secondary market
                          </p>
                        </div>
                      }
                      placement="right"
                    >
                      <QuestionCircleOutlined className="tooltip-icon" />
                    </Tooltip>
                  </>
                }
                name="tradeUnlockTime"
                getValueFromEvent={(date) => (date ? Math.floor(date.valueOf() / 1000) : null)}
                getValueProps={(value) => ({
                  value: value ? convertNumberToDateTime(value) : null,
                })}
              >
                <DatePicker className="w-full" disabled={!edit} />
              </Form.Item>
            </Col>
          </Row>
        </Col>

        <Col span={12}>
          <Row>
            {/* Selling Status */}
            {!isModeCreate ? (
              <Col span={6}>
                <Form.Item
                  label={
                    <>
                      Selling Status
                      <Tooltip
                        title={
                          <div className="tooltip-box">
                            <p className="tooltip-text">
                              Off Sale: PTO has not been listed for sale. On Sale: PTO is listed for
                              sale. Sold Out: all PTOs are sold.
                            </p>
                          </div>
                        }
                        placement="right"
                      >
                        <QuestionCircleOutlined className="tooltip-icon" />
                      </Tooltip>
                    </>
                  }
                >
                  <Tag color={sellingStatusConfig.color}>{sellingStatusConfig.label}</Tag>
                </Form.Item>
              </Col>
            ) : null}
            {/* Display */}
            <Col span={6}>
              <Form.Item
                label={
                  <>
                    Display
                    <Tooltip
                      title={
                        <div className="tooltip-box">
                          <p className="tooltip-text">
                            Choose to display PTO on user website or not. When the switch is{' '}
                            <strong>ON</strong>, show the PTO. When the switch is{' '}
                            <strong>OFF</strong>, hide the PTO.
                          </p>
                        </div>
                      }
                      placement="right"
                    >
                      <QuestionCircleOutlined className="tooltip-icon" />
                    </Tooltip>
                  </>
                }
                name="status"
                valuePropName={isModeCreate ? 'checked' : undefined}
                required
              >
                {isModeCreate ? (
                  <Switch />
                ) : (
                  <Switch checked={switchChecked} disabled={!edit} onClick={handleSwitchClick} />
                )}
              </Form.Item>
            </Col>
            {/* Feature */}
            <Col span={6}>
              <Form.Item
                label={
                  <>
                    Feature
                    <Tooltip
                      title={
                        <div className="tooltip-box">
                          <p className="tooltip-text">
                            Toggle the switch to manage your PTO . When the switch is{' '}
                            <strong>ON</strong>, the is active. When the switch is{' '}
                            <strong>OFF</strong>, the is inactive.
                          </p>
                        </div>
                      }
                      placement="right"
                    >
                      <QuestionCircleOutlined className="tooltip-icon" />
                    </Tooltip>
                  </>
                }
                name="isFeatured"
              >
                <Switch disabled={!edit} />
              </Form.Item>
            </Col>
            {/* isShowDetail */}
            <Col span={6}>
              <Form.Item
                label={
                  <>
                    Is Show detail
                    <Tooltip
                      title={
                        <div className="tooltip-box">
                          <p className="tooltip-text">
                            Toggle the switch to enable PTO detail view. When the switch is{' '}
                            <strong>ON</strong>, users can click the PTO to view the detail page.{' '}
                            When the switch is <strong>OFF</strong>, the detail page is disabled.{' '}
                            Default value is <strong>ON</strong>.
                          </p>
                        </div>
                      }
                      placement="right"
                    >
                      <QuestionCircleOutlined className="tooltip-icon" />
                    </Tooltip>
                  </>
                }
                name="isShowDetail"
              >
                <Switch disabled={!edit} />
              </Form.Item>
            </Col>
            {/* isShowInList */}
            <Col span={6}>
              <Form.Item
                label={
                  <>
                    Is Show In List
                    <Tooltip
                      title={
                        <div className="tooltip-box">
                          <p className="tooltip-text">
                            Toggle the switch to enable PTO to be shown in the list. When the switch
                            is <strong>ON</strong>, the PTO is visible in the list. When the switch
                            is <strong>OFF</strong>, the PTO is hidden from the list. Default value
                            is <strong>ON</strong>.
                          </p>
                        </div>
                      }
                      placement="right"
                    >
                      <QuestionCircleOutlined className="tooltip-icon" />
                    </Tooltip>
                  </>
                }
                name="isShowList"
              >
                <Switch disabled={!edit} />
              </Form.Item>
            </Col>
          </Row>
        </Col>

        {/* YouTube Link */}
        <Col span={12}>
          <Form.Item
            label={
              <>
                YouTube
                <Tooltip
                  title={
                    <div className="tooltip-box">
                      <p className="tooltip-text">
                        Enter the YouTube video or channel URL for this PTO
                      </p>
                    </div>
                  }
                  placement="right"
                >
                  <QuestionCircleOutlined className="tooltip-icon" />
                </Tooltip>
              </>
            }
            name={['typeSocial', 'youtube']}
            rules={[{ type: 'url', message: 'Please enter a valid URL!' }]}
          >
            <Input
              style={{ color: 'rgba(0, 0, 0, 0.85)' }}
              disabled={!edit}
              placeholder="https://youtube.com/..."
            />
          </Form.Item>
        </Col>

        {/* Terms of Use */}
        <Col span={24}>
          <Form.Item
            label={
              <>
                Terms of Use
                <Tooltip
                  title={
                    <div className="tooltip-box">
                      <p className="tooltip-text">
                        This is your PTO Terms of Use, User need to accept these terms before
                        purchasing or renting the PTO.
                      </p>
                    </div>
                  }
                  placement="right"
                >
                  <QuestionCircleOutlined className="tooltip-icon" />
                </Tooltip>
              </>
            }
            name="terms"
          >
            <MarkdownEditor disabled={!edit} placeholder="Enter the Terms of Use" />
          </Form.Item>
        </Col>
      </Row>
    </fieldset>
  )
}

export default BaseInformation
